'use client';

import React, { useEffect, useState, useCallback } from 'react';
import {
  X,
  Phone,
  Calendar,
  ChevronRight,
  ShieldCheck,
  User,
  CheckCircle2,
  AlertCircle,
  Clock
} from 'lucide-react';
import { AppointmentDetailsModal } from './AppointmentDetailsModal';

interface MyBookingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandColor?: string;
  businessName?: string;
  businessSlug?: string;
  onRescheduleFromModal?: (booking: any) => void;
}

export const MyBookingsModal: React.FC<MyBookingsModalProps> = ({
  isOpen,
  onClose,
  brandColor = '#1e293b',
  businessName = 'Cerini',
  businessSlug,
  onRescheduleFromModal
}) => {
  // Pestañas: Próximas vs Pasadas
  const [tab, setTab] = useState<'proximas' | 'pasadas'>('proximas');

  // Estado de usuario / login
  const [clientPhone, setClientPhone] = useState<string>('');
  const [isPhoneIdentified, setIsPhoneIdentified] = useState<boolean>(false);
  const [phoneInput, setPhoneInput] = useState<string>('');

  // Lista de reservas
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Modal de Detalles de Cita (Inspección con 3 botones)
  const [selectedBookingForDetails, setSelectedBookingForDetails] = useState<any | null>(null);

  // Cargar reservas sincronizadas con scope de negocio estricto
  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const storageKey = businessSlug ? `calenddy_bookings_${businessSlug}` : 'calenddy_my_bookings';
      const stored = localStorage.getItem(storageKey);
      let localList: any[] = [];
      if (stored) {
        try {
          localList = JSON.parse(stored);
        } catch (e) {
          console.error(e);
        }
      }

      // Si no es un array válido, asegurar lista vacía (sin inyectar citas de otros comercios)
      if (!Array.isArray(localList)) {
        localList = [];
      }

      // 2. Sincronizar estados con el servidor si existen IDs
      const validIds = localList.map((b) => b.id).filter(Boolean);
      if (validIds.length > 0) {
        const tokens = localList
          .filter((b) => b.id && (b.accessToken || b.securityCode))
          .map((b) => `${b.id}:${b.accessToken || b.securityCode}`)
          .join(',');
        
        try {
          const queryParams = new URLSearchParams({
            ids: validIds.join(','),
            ...(tokens ? { tokens } : {}),
            ...(businessSlug ? { slug: businessSlug } : {})
          });
          const res = await fetch(`/api/bookings?${queryParams.toString()}`);
          const data = await res.json();
          if (data.success && Array.isArray(data.appointments)) {
            // Unir estado del servidor
            const merged = localList.map((loc) => {
              const remote = data.appointments.find((r: any) => r.id === loc.id);
              if (remote) {
                return {
                  ...loc,
                  status: remote.status.toUpperCase(),
                  date: remote.dateStr || remote.date || loc.date,
                  dateStr: remote.dateStr || remote.date || loc.dateStr,
                  time: remote.startTime || remote.time || loc.time,
                  startTime: remote.startTime || remote.time || loc.startTime,
                  staffName: remote.staffName || loc.staffName
                };
              }
              return loc;
            });
            setBookings(merged);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.warn('Error fetching bookings from server:', e);
        }
      }

      setBookings(localList);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadBookings();
    }
  }, [isOpen, loadBookings]);

  if (!isOpen) return null;

  // Filtrar según pestaña Próximas vs Pasadas
  const todayStr = new Date().toISOString().split('T')[0];
  const proximas = bookings.filter((b) => {
    const d = b.dateStr || b.date || '';
    return d >= todayStr || b.status === 'CONFIRMED' || b.status === 'CANCELLED';
  });

  const pasadas = bookings.filter((b) => {
    const d = b.dateStr || b.date || '';
    return d < todayStr && b.status !== 'CONFIRMED' && b.status !== 'CANCELLED';
  });

  const displayedList = tab === 'proximas' ? proximas : pasadas;

  // Manejar cuando se cancela una cita desde el modal de detalles
  const handleBookingCancelled = (cancelledId: string) => {
    setBookings((prev) =>
      prev.map((b) => (b.id === cancelledId ? { ...b, status: 'CANCELLED' } : b))
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-in fade-in duration-200">
        <div className="bg-white rounded-3xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl border border-gray-100 animate-in zoom-in-95 duration-200">
          
          {/* HEADER DEL MODAL: "Tus citas" */}
          <div className="px-6 pt-6 pb-2 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 leading-tight">
                Tus citas
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {isPhoneIdentified ? `Gestión de citas para ${clientPhone}` : 'Autogestión de citas y reservas'}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-900 transition cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* PESTAÑAS (Fiel a Cerini: Línea negra inferior para la pestaña activa) */}
          <div className="px-6 pt-3 flex items-center space-x-6 border-b border-gray-100">
            <button
              type="button"
              onClick={() => setTab('proximas')}
              className={`pb-3 text-sm font-semibold transition cursor-pointer relative ${
                tab === 'proximas'
                  ? 'text-gray-900 border-b-2 border-black'
                  : 'text-gray-400 hover:text-gray-700'
              }`}
            >
              <span>Próximas</span>
              <span className="ml-1.5 text-xs text-gray-400">({proximas.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setTab('pasadas')}
              className={`pb-3 text-sm font-semibold transition cursor-pointer relative ${
                tab === 'pasadas'
                  ? 'text-gray-900 border-b-2 border-black'
                  : 'text-gray-400 hover:text-gray-700'
              }`}
            >
              <span>Pasadas</span>
              <span className="ml-1.5 text-xs text-gray-400">({pasadas.length})</span>
            </button>
          </div>

          {/* LISTADO DE CITAS */}
          <div className="flex-1 overflow-y-auto p-6 space-y-3">
            {loading ? (
              <div className="space-y-3 py-6">
                <div className="h-18 bg-gray-100 rounded-2xl animate-pulse" />
                <div className="h-18 bg-gray-100 rounded-2xl animate-pulse" />
              </div>
            ) : displayedList.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-800">
                  No tenés citas {tab === 'proximas' ? 'próximas' : 'pasadas'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Cuando reserves un servicio podrás visualizarlo y autogestionarlo aquí.
                </p>
              </div>
            ) : (
              displayedList.map((booking) => {
                const isCancelled = booking.status === 'CANCELLED' || booking.status === 'cancelled';
                
                // Formateo de fecha para el Badge (Mes en 3 letras y Día en número grande)
                let monthShort = 'OCT';
                let dayNum = '16';
                try {
                  const parts = (booking.dateStr || booking.date || '').split('-');
                  if (parts.length === 3) {
                    const year = parseInt(parts[0]);
                    const month = parseInt(parts[1]) - 1;
                    const day = parseInt(parts[2]);
                    const d = new Date(year, month, day, 12, 0, 0);
                    monthShort = d.toLocaleDateString('es-AR', { month: 'short' }).replace('.', '').toUpperCase();
                    dayNum = day.toString();
                  }
                } catch (e) {
                  console.error(e);
                }

                const startTimeFormatted = booking.startTime || booking.time || '10:00';

                return (
                  <div
                    key={booking.id}
                    onClick={() => setSelectedBookingForDetails(booking)}
                    className={`flex items-center justify-between p-4 bg-white rounded-2xl border transition-all cursor-pointer shadow-xs group ${
                      isCancelled
                        ? 'border-gray-100 opacity-75 hover:border-gray-200'
                        : 'border-gray-100 hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    {/* LADO IZQUIERDO: BADGE DE FECHA + TEXTO DEL TURNO */}
                    <div className="flex items-center space-x-3.5 min-w-0 flex-1">
                      {/* Badge Cuadrado con Mes y Día en tono rosa suave fiel a Calendico */}
                      <div className="w-13 h-13 rounded-xl flex flex-col items-center justify-center shrink-0 bg-[#fff1f2] border border-[#ffe4e6]/70">
                        <span className="text-[10px] font-bold tracking-wider uppercase leading-none text-[#f43f5e]">
                          {monthShort}
                        </span>
                        <span className="text-base font-bold leading-tight mt-0.5 text-[#e11d48]">
                          {dayNum}
                        </span>
                      </div>

                      {/* Información de Hora y Servicio */}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-400 font-medium">
                          {startTimeFormatted} AM
                        </p>
                        <h4
                          className={`text-sm leading-snug line-clamp-2 ${
                            isCancelled
                              ? 'text-gray-400 line-through font-normal'
                              : 'text-gray-900 font-semibold group-hover:text-black'
                          }`}
                        >
                          {booking.serviceName || 'Corte de hombre'} con {booking.staffName || 'Sebastián'}
                        </h4>
                      </div>
                    </div>

                    {/* LADO DERECHO: CHEVRON ELEGANTE */}
                    <div className="flex items-center shrink-0 ml-3">
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>
      </div>

      {/* Modal de Detalles de Cita (Lápiz Reagendar + WhatsApp + Calendario + Cancelar) */}
      {selectedBookingForDetails && (
        <AppointmentDetailsModal
          isOpen={Boolean(selectedBookingForDetails)}
          onClose={() => setSelectedBookingForDetails(null)}
          booking={selectedBookingForDetails}
          businessName={businessName}
          businessSlug={businessSlug}
          branchName={selectedBookingForDetails.branchName || 'Sede Principal'}
          onReschedule={(booking) => {
            setSelectedBookingForDetails(null);
            onClose();
            if (onRescheduleFromModal) {
              onRescheduleFromModal(booking);
            }
          }}
          onCancelled={handleBookingCancelled}
        />
      )}
    </>
  );
};
